import DriverWithoutTransport from './DriverWithoutTransport/DriverWithoutTransport.tsx'
import DriverQrPass from './DriverQrPass/DriverQrPass.tsx'
import LocalizedFileInput from 'components/Erp/LocalizedFileInput';
import DriverBlockReason from 'components/Erp/DriverBlockReason/DriverBlockReason.tsx';
import { tr } from 'i18n';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import { TripIntegrationDetails, isBusyDriver, QueueNumber, STAGES } from 'components/Erp/IntegrationTrip';
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Building2, Truck, UserRound, Clock3, RefreshCw, ArrowRightLeft, Search, Upload, Paperclip, LockKeyhole, Camera, X, ChevronDown, ChevronUp, History, Send, Factory, Landmark, ArrowUpRight, SquarePen, Phone, Car, CircleCheck, CircleX, ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import { DateTime, Vehicle, StatusBadge } from 'components/Erp/Display';
import { SectionHeading, EmptyState } from 'components/Erp/Presentation';
import UserAvatar from 'components/Erp/UserAvatar';
import $api from 'http';
import styles from './DriverCabinet.module.scss';
import Notification from 'utils/Notifications';
import {
  getDriverCabinet,
  resubmitDriverChecked,
  createBaseRequest,
  getDriverMyLoads,
  createDocChangeRequest,
  getMyDocChangeRequests,
  getDriverCheckedMy,
  getMyBaseRequests,
} from 'service/DriverRegistrationService';
const HIDDEN_BASE_NAMES = ["Черный список"];

const filterHiddenBases = (list) => list.filter(b => !HIDDEN_BASE_NAMES.includes(b.name));

const matchesBaseSearch = (list, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return list;
  return list.filter(b => (b.name || '').toLowerCase().includes(q));
};

const DriverCabinet = ({ user }) => {
  useInterfaceLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [loadsError, setLoadsError] = useState('');
  const [basesError, setBasesError] = useState('');
  const [baseRequests, setBaseRequests] = useState([]);
  const [requestsError, setRequestsError] = useState('');
  const [showRequests, setShowRequests] = useState(false);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });

  // Base selection
  const [bases, setBases] = useState({ topbases: [], chpps: [], privatepoints: [] });
  const [basesLoading, setBasesLoading] = useState(false);
  const [selectedBaseType, setSelectedBaseType] = useState('topbase');
  const [selectedBaseId, setSelectedBaseId] = useState(null);
  const [baseSearch, setBaseSearch] = useState('');
  const [submittingBaseRequest, setSubmittingBaseRequest] = useState(false);

  // Change base modal
  const [showChangeBase, setShowChangeBase] = useState(false);
  const [changeSelectedBaseType, setChangeSelectedBaseType] = useState('topbase');
  const [changeSelectedBaseId, setChangeSelectedBaseId] = useState(null);
  const [changeBaseSearch, setChangeBaseSearch] = useState('');

  // Resubmit
  const [resubmitting, setResubmitting] = useState(false);

  // My docs
  const [showMyDocs, setShowMyDocs] = useState(false);
  const [myDcRecord, setMyDcRecord] = useState(null);
  const [docChangeTarget, setDocChangeTarget] = useState(null);
  const [docChangeFile, setDocChangeFile] = useState(null);
  const [docChangePreviewUrl, setDocChangePreviewUrl] = useState(null);
  const [submittingDocChange, setSubmittingDocChange] = useState(false);
  const [myDocReqs, setMyDocReqs] = useState([]);
  const [docPreviewOpen, setDocPreviewOpen] = useState(null);

  // Loads
  const [loads, setLoads] = useState(null);
  const [loadsLoading, setLoadsLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);

  const loadCabinet = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await getDriverCabinet();
      setData(res.data);
    } catch (e) {
      setLoadError(tr("Не удалось загрузить личный кабинет. Повторите загрузку."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCabinet();
  }, [loadCabinet]);

  const loadMyLoads = useCallback(async (page = 1) => {
    setLoadsLoading(true);
    setLoadsError('');
    try {
      const res = await getDriverMyLoads({ page, page_size: 10 });
      setLoads(res.data);
    } catch (e) {
      setLoadsError(tr("Не удалось загрузить ваши погрузки."));
    } finally {
      setLoadsLoading(false);
    }
  }, []);

  const loadBases = useCallback(async () => {
    setBasesLoading(true);
    try {
      setBasesError('');
      const response = await $api.get('/tabel/registration-locations/');
      const locations = response.data?.results || response.data || [];
      setBases({ topbases: filterHiddenBases(locations.filter(base => base.type === 'top_base')), chpps: filterHiddenBases(locations.filter(base => base.type === 'chpp')), privatepoints: filterHiddenBases(locations.filter(base => base.type === 'private_point')) });
    } catch {
      setBasesError(tr("Не удалось загрузить список баз. Повторите запрос."));
    } finally {
      setBasesLoading(false);
    }
  }, []);

  const loadMyRequests = useCallback(async () => {
    setRequestsError('');
    try { const response = await getMyBaseRequests(); setBaseRequests(response.data?.results || response.data || []); }
    catch { setRequestsError(tr("Не удалось загрузить историю заявок.")); }
  }, []);

  useEffect(() => {
    if (!docChangePreviewUrl) return;
    return () => URL.revokeObjectURL(docChangePreviewUrl);
  }, [docChangePreviewUrl]);

  const loadMyDocReqs = useCallback(async () => {
    try {
      const res = await getMyDocChangeRequests();
      setMyDocReqs(res.data?.results || res.data || []);
    } catch (e) {
      console.error('Failed to load doc change requests', e);
    }
  }, []);

  const loadMyDc = useCallback(async () => {
    try {
      const res = await getDriverCheckedMy();
      setMyDcRecord(res.data || null);
    } catch (e) {
      console.error('Failed to load driver_checked', e);
    }
  }, []);

  useEffect(() => {
    if (data?.driver && !data?.driver?.top_base && !data?.driver?.chpp && !data?.driver?.private_point) {
      loadBases();
    }
    if (showChangeBase) {
      setChangeBaseSearch('');
      setChangeSelectedBaseId(null);
      loadBases();
    }
  }, [data, showChangeBase, loadBases]);

  useEffect(() => {
    if (data?.driver || data?.driver_person) {
      loadMyLoads(historyPage);
    }
  }, [data?.driver, data?.driver_person, historyPage, loadMyLoads]);

  useEffect(() => {
    if (data?.driver) {
      loadMyDocReqs();
      loadMyRequests();
      if (!data?.driver_checked) {
        loadMyDc();
      }
    }
  }, [data?.driver, data?.driver_checked, loadMyDocReqs, loadMyDc, loadMyRequests]);

  const handleSubmitJoinRequest = async () => {
    if (!selectedBaseId) {
      setNotify({ isOpen: true, message: tr("Выберите базу"), type: 'warning' });
      return;
    }
    setSubmittingBaseRequest(true);
    try {
      const payload = { request_type: 'join' };
      if (selectedBaseType === 'topbase') payload.top_base = selectedBaseId;
      else if (selectedBaseType === 'chpp') payload.chpp = selectedBaseId;
      else payload.private_point = selectedBaseId;
      await createBaseRequest(payload);
      setNotify({ isOpen: true, message: tr("Заявка отправлена! Ожидайте ответа оператора."), type: 'success' });
      await loadCabinet();
    } catch (e) {
      setNotify({ isOpen: true, message: e.response?.data?.detail || tr("Ошибка"), type: 'error' });
    } finally {
      setSubmittingBaseRequest(false);
    }
  };

  const handleSubmitLeaveRequest = async () => {
    if (!changeSelectedBaseId) {
      setNotify({ isOpen: true, message: tr("Выберите новую базу"), type: 'warning' });
      return;
    }
    setSubmittingBaseRequest(true);
    try {
      const payload = { request_type: 'leave' };
      if (changeSelectedBaseType === 'topbase') payload.new_top_base = changeSelectedBaseId;
      else if (changeSelectedBaseType === 'chpp') payload.new_chpp = changeSelectedBaseId;
      else payload.new_private_point = changeSelectedBaseId;
      await createBaseRequest(payload);
      setNotify({ isOpen: true, message: tr("Запрос на смену базы отправлен."), type: 'success' });
      setShowChangeBase(false);
      await loadCabinet();
    } catch (e) {
      setNotify({ isOpen: true, message: e.response?.data?.detail || tr("Ошибка"), type: 'error' });
    } finally {
      setSubmittingBaseRequest(false);
    }
  };

  const handleResubmit = async () => {
    setResubmitting(true);
    try {
      const fd = new FormData();
      await resubmitDriverChecked(fd);
      setNotify({ isOpen: true, message: tr("Заявка повторно отправлена на рассмотрение."), type: 'success' });
      await loadCabinet();
    } catch (e) {
      setNotify({ isOpen: true, message: e.response?.data?.detail || tr("Ошибка"), type: 'error' });
    } finally {
      setResubmitting(false);
    }
  };

  const selectDocument = (file) => {
    if (file && !file.type.startsWith('image/') && file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setNotify({ isOpen: true, message: tr("Выберите фотографию или PDF-документ."), type: 'warning' }); return;
    }
    setDocChangeFile(file || null);
    setDocChangePreviewUrl(file?.type.startsWith('image/') ? URL.createObjectURL(file) : null);
  };

  const handleDocChangeSubmit = async () => {
    if (!docChangeFile || !docChangeTarget) return;
    setSubmittingDocChange(true);
    try {
      const fd = new FormData();
      fd.append('field_name', docChangeTarget.field);
      fd.append('field_label', docChangeTarget.label);
      fd.append('new_file', docChangeFile);
      await createDocChangeRequest(fd);
      setNotify({ isOpen: true, message: tr("Заявка на смену документа отправлена. Ожидайте подтверждения."), type: 'success' });
      setDocChangeTarget(null);
      setDocChangeFile(null);
      setDocChangePreviewUrl(null);
      await loadCabinet();
      await loadMyDocReqs();
    } catch (e) {
      setNotify({ isOpen: true, message: e.response?.data?.detail || tr("Ошибка при отправке заявки"), type: 'error' });
    } finally {
      setSubmittingDocChange(false);
    }
  };

  if (loading) return <div className={styles.loadingState}>{tr("Загрузка личного кабинета...")}</div>;
  if (!data) return <div className={styles.errorState} role="alert"><p>{tr(loadError || tr("Профиль водителя недоступен."))}</p><button className={styles.btnPrimary} onClick={loadCabinet}><RefreshCw size={16} aria-hidden="true" />{tr("Повторить загрузку")}</button></div>;

  const dc = data.driver_checked || myDcRecord;
  const driver = data.driver;
  const pendingBR = data.pending_base_request;

  const fio = (dc?.last_name && dc?.first_name)
    ? `${dc.last_name} ${dc.first_name}${dc.middle_name ? ' ' + dc.middle_name : ''}`
    : driver
    ? `${driver.employee_name || ''}`
    : data?.driver_person?.name || tr("Водитель");

  const isBlocked = driver?.status === 'Заблокирован' || driver?.is_active === false;
  const isBusy = isBusyDriver(driver?.status);
  const isFree = driver?.status === 'Свободен';
  const isUnderDocReview = driver?.status === 'На рассмотрении';
  const documentUrl = field => driver?.[`${field}_url`] || driver?.[field] || dc?.[`${field}_url`] || dc?.[field];

  const DOC_FIELDS = [
    { field: 'photo_vehicle',       label: "Фотография транспорта" },
    { field: 'passport_front',      label: "Паспорт · лицевая сторона" },
    { field: 'passport_back',       label: "Паспорт · оборотная сторона" },
    { field: 'license_front',       label: "Водительское удостоверение · лицевая сторона" },
    { field: 'license_back',        label: "Водительское удостоверение · оборотная сторона" },
    { field: 'tech_passport_front', label: "Техпаспорт · лицевая сторона" },
    { field: 'tech_passport_back',  label: "Техпаспорт · оборотная сторона" },
  ].map(document => ({ ...document, url: documentUrl(document.field) }));

  const currentBase = driver?.top_base_name || driver?.chpp_name || driver?.private_point_name;
  const currentBaseType = driver?.top_base ? tr("ТОП-База") : driver?.chpp ? tr("ТЭЦ") : driver?.private_point ? tr("Соц. объект / МСУ") : null;

  const getDriverBadgeClass = (st) => {
    if (!st) return styles.badge;
    if (st === 'Свободен') return `${styles.badge} ${styles.badgeFree}`;
    if (st === 'Заблокирован') return `${styles.badge} ${styles.badgeBlocked}`;
    if (st === 'На рассмотрении') return `${styles.badge} ${styles.badgePending}`;
    return `${styles.badge} ${styles.badgeLoading}`;
  };

  const currentBases = selectedBaseType === 'topbase' ? bases.topbases
    : selectedBaseType === 'chpp' ? bases.chpps
    : bases.privatepoints;

  const changeBases = changeSelectedBaseType === 'topbase' ? bases.topbases
    : changeSelectedBaseType === 'chpp' ? bases.chpps
    : bases.privatepoints;

  const visibleCurrentBases = matchesBaseSearch(currentBases, baseSearch);

  const isCurrentDriverBase = (b) =>
    changeSelectedBaseType === 'topbase' ? driver?.top_base === b.id
    : changeSelectedBaseType === 'chpp' ? driver?.chpp === b.id
    : driver?.private_point === b.id;

  const availableChangeBases = changeBases.filter(b => !isCurrentDriverBase(b));
  const visibleChangeBases = matchesBaseSearch(availableChangeBases, changeBaseSearch);

  return (
    <div className={styles.cabinet}>
      {!driver && data?.driver_person && <DriverWithoutTransport />}
      {tr(loadError && <div className={styles.errorState} role="alert">{tr(loadError)}<button onClick={loadCabinet}><RefreshCw size={16} aria-hidden="true" />{tr("Повторить")}</button></div>)}
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerRow}>
          <UserAvatar user={user || { surname: dc?.last_name || driver?.employee_name, first_name: dc?.first_name }} className={styles.avatarPlaceholder} />
          <div className={styles.headerInfo}>
            <h2 className={styles.userName}>{tr(fio)}</h2>
            <p className={styles.userInn}>{tr("UID:")}{tr(driver?.uid || driver?.qrcode_scr || dc?.inn || data?.driver_person?.inn || '—')}</p>
            {tr(driver && (
              <span className={getDriverBadgeClass(driver.status)}>
                {isBlocked ? <LockKeyhole size={13} aria-hidden="true" /> : isBusy ? <Truck size={13} aria-hidden="true" /> : <CircleCheck size={13} aria-hidden="true" />}{tr(driver.status || tr("Свободен"))}
              </span>
            ))}
            {tr(!driver && dc && (
              <span className={`${styles.badge} ${dc.status === 'pending' ? styles.badgePending : dc.status === 'approved' ? styles.badgeApproved : styles.badgeRejected}`}>
                {dc.status === 'pending' ? tr("На рассмотрении")
                  : dc.status === 'approved' ? tr(" Одобрено")
                  : tr(" Отказано")}
              </span>
            ))}
          </div>
        </div>
        <div className={styles.cabinetToolbar}><p className={styles.systemTitle}><Truck size={16} aria-hidden="true" />{tr("Личный кабинет водителя")}</p><button className={styles.btnSecondary} onClick={loadCabinet}><RefreshCw size={16} />{tr("Обновить")}</button></div>
      </div>

      {/* ── QR Pass ── */}
      {tr(driver?.uid && currentBase && !isBlocked && !isUnderDocReview && (
        <DriverQrPass uid={driver.uid} className={styles.card} />
      ))}

      {/* ── Registration status ── */}
      {tr(dc && dc.status === 'pending' && !driver && (
        <div className={styles.card}>
          <SectionHeading icon={Clock3} title={tr("Заявка на рассмотрении")} tone="amber" as="h3" />
          <p style={{ fontSize: 14, color: "var(--theme-text-muted, #6b7280)", margin: 0, lineHeight: 1.6 }}>{tr("Ваша заявка отправлена в администрацию ОАО \"Кыргызкомур\". После проверки документов и данных вы получите доступ к полному функционалу. Пожалуйста, ожидайте подтверждения.")}</p>
        </div>
      ))}

      {/* ── Rejected ── */}
      {tr(dc && dc.status === 'rejected' && !driver && (
        <>
          <div className={styles.rejectedBlock}>
            <p className={styles.rejectedTitle}><CircleX size={17} aria-hidden="true" />{tr("Заявка отклонена")}</p>
            <p className={styles.rejectedReason}>
              <strong>{tr("Причина:")}</strong> {tr(dc.rejection_reason || tr("Причина не указана"))}
            </p>
            <p className={styles.rejectedContact}>{tr("При возникновении вопросов обратитесь по номеру: ")}<strong>0600011032</strong>
            </p>
          </div>
          <div className={styles.card}>
            <SectionHeading icon={ArrowRightLeft} title={tr("Повторная подача")} tone="primary" as="h3" />
            <p style={{ fontSize: 13, color: "var(--theme-text-muted, #6b7280)", marginBottom: 12 }}>{tr("Вы можете исправить данные и отправить заявку повторно.")}</p>
            <Link className={styles.btnSecondary} to="/driver-register/?edit=1"><SquarePen size={16} aria-hidden="true" />{tr("Исправить заявку")}</Link>
            <button
              className={styles.btnPrimary}
              onClick={handleResubmit}
              disabled={resubmitting}
            >
              <Send size={16} aria-hidden="true" />{resubmitting ? tr("Отправка...") : tr("Отправить повторно")}
            </button>
          </div>
        </>
      ))}

      {/* ── Driver profile info ── */}
      {tr(driver && (
        <div className={styles.card}>
          <SectionHeading icon={UserRound} title={tr("Профиль водителя")} tone="primary" as="h3" />
          <div className={styles.infoGrid}>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}><UserRound size={13} aria-hidden="true" />{tr("ФИО")}</span>
              <span className={styles.infoValue}>{tr(driver.employee_name || fio)}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}><Phone size={13} aria-hidden="true" />{tr("Телефон")}</span>
              <span className={styles.infoValue}>{tr(driver.phone || driver.employee_phone || dc?.phone || '—')}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}><Car size={13} aria-hidden="true" />{tr("Гос. номер")}</span>
              <Vehicle number={driver.vehicle_number} model={driver.vehicle_model} />
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}><Truck size={13} aria-hidden="true" />{tr("Марка / модель")}</span>
              <span className={styles.infoValue}>{tr(driver.vehicle_model || '—')}</span>
            </div>
          </div>
          {(
            <div className={styles.actionsRow} style={{ marginTop: 12 }}>
              <button
                className={styles.btnSecondary}
                onClick={() => { setShowMyDocs(d => !d); setDocPreviewOpen(null); }}
              >
                <FileText size={16} />{showMyDocs ? tr("Скрыть документы") : tr("Мои документы")}
              </button>
            </div>
          )}
        </div>
      ))}

      {/* ── Under doc review notice ── */}
      {tr(driver && isUnderDocReview && (
        <div className={styles.card} style={{ borderLeft: "4px solid var(--theme-warning-border, #f59e0b)" }}>
          <SectionHeading icon={Clock3} title={tr("Заявка на смену документа")} tone="amber" as="h3" />
          <p style={{ fontSize: 14, color: "var(--theme-text-muted, #6b7280)", margin: 0, lineHeight: 1.6 }}>{tr("Одна или несколько заявок на смену документов отправлены на рассмотрение. QR-пропуск временно недоступен. Ожидайте подтверждения администратором. Остальные документы вы можете продолжать подавать заявки на смену.")}</p>
        </div>
      ))}

      {/* ── My documents section ── */}
      {tr(driver && showMyDocs && (
        <div className={styles.card}>
          <SectionHeading icon={FileText} title={tr("Мои документы")} tone="primary" as="h3" />
          {(() => {
            const pendingByField = {};
            myDocReqs.filter(r => r.status === 'pending').forEach(r => { pendingByField[r.field_name] = r; });
            return (
              <>
                {DOC_FIELDS.map(({ field, label, url }) => {
                  const pending = pendingByField[field];
                  const pendingUrl = pending?.new_file_url || pending?.new_file;
                  return (
                    <div key={field} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0', borderBottom: "1px solid var(--theme-border, #f1f5f9)" }}>
                      <div style={{ flex: 1 }}>
                        <p style={{ margin: 0, fontSize: 11, color: "var(--theme-text-subtle, #9ca3af)", textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.04em' }}>{tr(label)}</p>
                        <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                          {url ? (
                            <button
                              onClick={() => setDocPreviewOpen(docPreviewOpen === url ? null : url)}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, border: docPreviewOpen === url ? "1.5px solid var(--theme-border-strong, #374151)" : "1px solid var(--theme-border, #d1d5db)", background: docPreviewOpen === url ? "var(--theme-info-soft, #f0f4ff)" : "var(--theme-surface, #fff)", color: "var(--theme-text, #374151)", cursor: 'pointer', fontSize: 12 }}>
                              <Paperclip size={14} />{tr(" Текущий")}</button>
                          ) : (
                            <span style={{ fontSize: 12, color: "var(--theme-text-subtle, #9ca3af)", fontStyle: 'italic' }}>{tr("Не загружен")}</span>
                          )}
                          {tr(pending && (
                            <>
                              <span style={{ fontSize: 12, color: "var(--theme-text-subtle, #94a3b8)" }}>→</span>
                              <span style={{ background: "var(--theme-warning-soft, #fef9c3)", color: "var(--theme-orange-text, #92400e)", borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{tr("На рассмотрении")}</span>
                              <button
                                onClick={() => setDocPreviewOpen(docPreviewOpen === pendingUrl ? null : pendingUrl)}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, border: docPreviewOpen === pendingUrl ? "1.5px solid var(--theme-orange-border, #92400e)" : "1px solid var(--theme-warning-border, #fde68a)", background: docPreviewOpen === pendingUrl ? "var(--theme-warning-soft, #fef9c3)" : "var(--theme-warning-soft, #fffbeb)", color: "var(--theme-orange-text, #92400e)", cursor: 'pointer', fontSize: 12 }}>
                                <Paperclip size={14} />{tr(" Новый")}</button>
                            </>
                          ))}
                        </div>
                      </div>
                      {tr(!isBlocked && !isBusy && !pending && (
                        <button
                          onClick={() => { setDocChangeTarget({ field, label, currentUrl: url }); setDocChangeFile(null); setDocChangePreviewUrl(null); }}
                          style={{ flexShrink: 0, padding: '6px 12px', borderRadius: 8, border: url ? "1.5px solid var(--theme-primary-border, #c1bdff)" : "1.5px solid var(--theme-success-border, #16a34a)", background: "var(--theme-surface, #fff)", color: url ? "var(--theme-primary-text, #0d00f3)" : "var(--theme-success-text, #16a34a)", cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                          <Upload size={14} />{url ? tr("Сменить") : tr("Загрузить")}
                        </button>
                      ))}
                    </div>
                  );
                })}
                {tr(docPreviewOpen && (
                  <div style={{ marginTop: 12, border: "1px solid var(--theme-border, #e5e7eb)", borderRadius: 8, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 12px', background: "var(--theme-surface-muted, #f1f5f9)" }}>
                      <span className={styles.previewTitle}><Eye size={15} aria-hidden="true" />{tr("Просмотр")}</span>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <a href={docPreviewOpen} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: "var(--theme-primary-text, #0d00f3)", textDecoration: 'none' }}><ArrowUpRight size={14} aria-hidden="true" />{tr("Открыть")}</a>
                        <button onClick={() => setDocPreviewOpen(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: "var(--theme-text-muted, #6b7280)", fontSize: 16 }} aria-label={tr("Закрыть просмотр")}><X size={16} /></button>
                      </div>
                    </div>
                    {docPreviewOpen.toLowerCase().includes('.pdf') ? (
                      <iframe src={docPreviewOpen} title={tr("Предпросмотр документа")} style={{ width: '100%', height: 400, border: 'none', display: 'block' }} />
                    ) : (
                      <img src={docPreviewOpen} alt={tr("Предпросмотр документа")} style={{ width: '100%', maxHeight: 400, objectFit: 'contain', display: 'block' }} />
                    )}
                  </div>
                ))}
              </>
            );
          })()}
        </div>
      ))}

      {/* ── Blocked ── */}
      {tr(isBlocked && (
        <div className={styles.rejectedBlock}>
          <p className={styles.rejectedTitle}><LockKeyhole size={17} />{tr(" Аккаунт заблокирован")}</p>
          <DriverBlockReason reason={driver.blocked_reason} />
          <p className={styles.rejectedContact}>{tr("Контакт: ")}<strong>0600011032</strong></p>
        </div>
      ))}

      {/* ── Pending base request ── */}
      {tr(driver && pendingBR && (
        <div className={styles.card}>
          <SectionHeading icon={FileText} title={tr("Активная заявка")} tone="primary" as="h3" />
          <div className={styles.pendingRequestBlock}>
            <div className={styles.pendingRequestIcon}>
              <ArrowRightLeft size={20} />
            </div>
            <div className={styles.pendingRequestText}>
              <p className={styles.pendingRequestTitle}>
                {pendingBR.request_type === 'join'
                  ? tr("Запрос на вступление в \"{{value0}}\"", { value0: pendingBR.base_name || '—' })
                  : tr("Запрос на выход из \"{{value0}}\"", { value0: pendingBR.base_name || '—' })}
              </p>
              <p className={styles.pendingRequestSub}>
                {pendingBR.request_type === 'leave' && pendingBR.new_base_name
                  ? tr("Новая база: {{value0}}", { value0: pendingBR.new_base_name })
                  : tr("Ожидание подтверждения оператора")}
              </p>
            </div>
            <span className={`${styles.badge} ${styles.badgePending}`}>{tr("На рассмотрении")}</span>
          </div>
        </div>
      ))}

      {/* ── Assigned base ── */}
      {tr(driver && !isBlocked && currentBase && !pendingBR && (
        <div className={styles.card}>
          <SectionHeading icon={Building2} title={tr("Текущая база")} tone="teal" as="h3" />
          <div className={styles.baseInfoBlock}>
            <div className={styles.baseInfoIcon}><Building2 size={23} /></div>
            <div>
              <p className={styles.baseInfoName}>{tr(currentBase)}</p>
              <p className={styles.baseInfoType}>{tr(currentBaseType)}</p>
            </div>
          </div>
          {tr(!isBusy && isFree && (
            <div className={styles.actionsRow}>
              <button
                className={styles.btnSecondary}
                onClick={() => setShowChangeBase(true)}
              >
                <ArrowRightLeft size={16} />{tr("Сменить базу")}</button>
            </div>
          ))}
          {tr(isBusy && (
            <p style={{ fontSize: 13, color: "var(--theme-orange-text, #92400e)", marginTop: 8 }}>{tr("Смена базы недоступна во время рейса (")}{tr(driver.status)}).
            </p>
          ))}
        </div>
      ))}

      {/* ── Base selection (approved but no base yet) ── */}
      {tr(driver && !isBlocked && !currentBase && !pendingBR && (
        <div className={styles.card}>
          <SectionHeading icon={Building2} title={tr("Выберите базу для работы")} tone="teal" as="h3" />
          <p style={{ fontSize: 13, color: "var(--theme-text-muted, #6b7280)", marginBottom: 14 }}>{tr("Ваш профиль подтверждён. Выберите базу, куда хотите подать заявку на работу.")}</p>
          {tr(basesError && <div className={styles.errorState} role="alert">{tr(basesError)}<button onClick={loadBases}>{tr("Повторить")}</button></div>)}
          <div className={styles.baseSelector}>
            {tr(basesError && <div className={styles.errorState} role="alert">{tr(basesError)}<button onClick={loadBases}>{tr("Повторить")}</button></div>)}
            <div className={styles.baseTabs}>
              {[{ key: 'topbase', label: "ТОП-Базы", icon: Building2 }, { key: 'chpp', label: "ТЭЦ", icon: Factory }, { key: 'privatepoint', label: "Соц. объекты / МСУ", icon: Landmark }].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  className={`${styles.baseTab} ${selectedBaseType === key ? styles.baseTabActive : ''}`}
                  onClick={() => { setSelectedBaseType(key); setSelectedBaseId(null); setBaseSearch(''); }}
                >
                  <Icon size={15} aria-hidden="true" />{tr(label)}
                </button>
              ))}
            </div>
            {basesLoading ? (
              <div className={styles.loadingState}>{tr("Загрузка...")}</div>
            ) : (
              <>
                <div className={styles.baseSearchWrap}>
                  <span className={styles.baseSearchIcon}><Search size={16} /></span>
                  <input
                    className={styles.baseSearchInput}
                    placeholder={tr("Поиск базы по названию...")}
                    value={baseSearch}
                    onChange={(e) => setBaseSearch(e.target.value)}
                  />
                  {tr(baseSearch && (
                    <button className={styles.baseSearchClear} onClick={() => setBaseSearch('')} aria-label={tr("Очистить поиск")}><X size={16} /></button>
                  ))}
                </div>
                {tr(currentBases.length > 0 && (
                  <div className={styles.baseCount}>
                    {baseSearch
                      ? tr("Найдено: {{value0}} из {{value1}}", { value0: visibleCurrentBases.length, value1: currentBases.length })
                      : tr("Всего: {{value0}}", { value0: currentBases.length })}
                  </div>
                ))}
                <div className={styles.baseList} role="radiogroup" aria-label={tr("Базы для вступления")}>
                  {currentBases.length === 0 ? (
                    <EmptyState icon={Building2} title={tr("Нет доступных баз")} description={tr("В этом разделе пока нет баз для подачи заявки.")} compact />
                  ) : visibleCurrentBases.length === 0 ? (
                    <EmptyState icon={Search} title={tr("Ничего не найдено по запросу «{{value0}}»", { value0: baseSearch })} description={tr("Проверьте название или выберите другой тип объекта.")} compact />
                  ) : visibleCurrentBases.map(b => (
                    <div
                      key={b.id}
                      role="radio" aria-checked={selectedBaseId === b.id} tabIndex={0}
                      onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setSelectedBaseId(b.id); } }}
                      className={`${styles.baseItem} ${selectedBaseId === b.id ? styles.baseItemSelected : ''}`}
                      onClick={() => setSelectedBaseId(b.id)}
                    >
                      <div className={`${styles.baseItemDot} ${selectedBaseId === b.id ? styles.baseItemDotSelected : ''}`} />
                      <span className={styles.baseItemName}>{tr(b.name)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
            <div className={styles.actionsRow}>
              <button
                className={styles.btnPrimary}
                onClick={handleSubmitJoinRequest}
                disabled={!selectedBaseId || submittingBaseRequest}
              >
                <Send size={16} aria-hidden="true" />{submittingBaseRequest ? tr("Отправка...") : tr("Подать заявку")}
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* ── Loads section ── */}
      {tr((driver || data?.driver_person) && (
        <div className={styles.card}>
          <SectionHeading icon={Truck} title={tr("Погрузки")} tone="blue" as="h3" />

          {tr(loadsError && !showHistory && <div className={styles.errorState} role="alert">{tr(loadsError)}<button onClick={() => loadMyLoads(historyPage)}>{tr("Повторить")}</button></div>)}
          {/* Active load */}
          {tr(loadsLoading && !loads && (
            <p style={{ fontSize: 13, color: "var(--theme-text-subtle, #9ca3af)" }}>{tr("Загрузка...")}</p>
          ))}
          {tr(loads && loads.active && loads.active.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <p style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', color: "var(--theme-text-muted, #6b7280)", margin: '0 0 8px', fontWeight: 600 }}>{tr("Активная погрузка")}</p>
              {loads.active.map(l => (
                <div key={l.id} style={{ background: "var(--theme-surface, #fafaff)", border: "1px solid var(--theme-primary-border, #c1bdff)", borderRadius: 10, padding: '12px 16px', marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
<StatusBadge tone={l.status === 'В пути' ? 'info' : 'warning'}>{tr(STAGES[l.stage] || l.status)}</StatusBadge><QueueNumber load={l} />
                    <span style={{ fontSize: 12, color: "var(--theme-text-muted, #6b7280)" }}><DateTime value={l.time_a} /></span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px' }}>
                    {[
                      [tr("База"), l.top_base_name || l.chpp_name || l.private_point_name || '—'],
                      [tr("Гос. номер"), l.vehicle_number || '—'],
                    ].map(([label, val]) => (
                      <div key={label}>
                        <p style={{ margin: 0, fontSize: 11, color: "var(--theme-text-subtle, #9ca3af)", textTransform: 'uppercase', letterSpacing: '0.04em' }}>{tr(label)}</p>
                        <p style={{ margin: '2px 0 0', fontSize: 13, fontWeight: 600, color: "var(--theme-text, #111827)" }}>{tr(val)}</p>
                      </div>
                    ))}
                  </div>
                  <TripIntegrationDetails load={l} compact />
                </div>
              ))}
            </div>
          ))}
          {tr(loads && loads.active && loads.active.length === 0 && (
            <EmptyState icon={Truck} title={tr("Нет активных погрузок")} description={tr("Новый рейс появится после отметки въезда в Кара-Кече.")} compact />
          ))}

          {/* History toggle */}
          <button
            onClick={() => { setShowHistory(h => !h); if (!showHistory && historyPage !== 1) setHistoryPage(1); }}
            style={{ background: "var(--theme-surface-muted, #f9fafb)", border: "1px solid var(--theme-border, #e5e7eb)", borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: "var(--theme-text, #374151)", width: '100%', textAlign: 'left' }}
          >
            <History size={16} aria-hidden="true" />{showHistory ? <ChevronUp size={16} /> : <ChevronDown size={16} />}{showHistory ? tr("Скрыть историю") : tr("История погрузок{{value0}}", { value0: loads?.history_count ? ` (${loads.history_count})` : '' })}
          </button>

          {tr(showHistory && (
            <div style={{ marginTop: 12 }}>
              {loadsError ? <div className={styles.errorState} role="alert">{tr(loadsError)}<button onClick={() => loadMyLoads(historyPage)}>{tr("Повторить")}</button></div> : loadsLoading ? (
                <p style={{ fontSize: 13, color: "var(--theme-text-subtle, #9ca3af)" }}>{tr("Загрузка...")}</p>
              ) : !loads?.history || loads.history.length === 0 ? (
                <EmptyState icon={History} title={tr("История пуста")} description={tr("Здесь будут отображаться ваши завершённые перевозки.")} compact />
              ) : (
                <>
                  {loads.history.map(l => (
                    <div key={l.id} style={{ border: "1px solid var(--theme-border, #e5e7eb)", borderRadius: 10, padding: '10px 14px', marginBottom: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ fontWeight: 600, fontSize: 13, color: "var(--theme-text, #374151)" }}>{tr(l.top_base_name || l.chpp_name || l.private_point_name || '—')}</span>
                        <span style={{ fontSize: 12, color: "var(--theme-text-muted, #6b7280)" }}><DateTime value={l.time_b} /></span>
                      </div>
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        {tr(l.duration_str && <span className={styles.duration}><Clock3 size={14} />{tr(l.duration_str)}</span>)}
                        {tr(l.vehicle_number && <Vehicle number={l.vehicle_number} model={l.vehicle_model} />)}
                      </div>
                      <TripIntegrationDetails load={l} compact />
                    </div>
                  ))}
                  {/* Pagination */}
                  {tr(loads.history_count > 10 && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 8 }}>
                      <button
                        onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                        disabled={historyPage === 1 || loadsLoading}
                        style={{ padding: '6px 14px', borderRadius: 7, border: "1px solid var(--theme-border, #e5e7eb)", background: "var(--theme-surface, #fff)", cursor: 'pointer', fontSize: 13 }}
                      ><ChevronLeft size={16} aria-hidden="true" />{tr("Назад")}</button>
                      <span style={{ fontSize: 13, color: "var(--theme-text-muted, #6b7280)", alignSelf: 'center' }}>{tr("Стр. ")}{tr(historyPage)}</span>
                      <button
                        onClick={() => setHistoryPage(p => p + 1)}
                        disabled={historyPage * 10 >= loads.history_count || loadsLoading}
                        style={{ padding: '6px 14px', borderRadius: 7, border: "1px solid var(--theme-border, #e5e7eb)", background: "var(--theme-surface, #fff)", cursor: 'pointer', fontSize: 13 }}
                      >{tr("Вперёд")}<ChevronRight size={16} aria-hidden="true" /></button>
                    </div>
                  ))}
                </>
              )}
            </div>
          ))}
        </div>
      ))}

      {tr(driver && <div className={`${styles.card} ${styles.requestsCard}`}>
        <div className={styles.requestHeading}><SectionHeading icon={FileText} title={tr("Мои заявки")} as="h3" /><button className={styles.btnSecondary} onClick={() => { setShowRequests(value => !value); if (!showRequests) loadMyRequests(); }}><History size={16} aria-hidden="true" />{showRequests ? tr("Скрыть историю") : tr("Открыть историю")}</button><Link className={styles.btnSecondary} to="/requests/"><ArrowUpRight size={16} aria-hidden="true" />{tr("Все заявки")}</Link></div>
        <p className={styles.smallNote}>{tr("Вступление и смена базы, проверка документов. Решения сохраняются в истории.")}</p>
        {tr(showRequests && <>{requestsError ? <div role="alert" className={styles.errorState}>{tr(requestsError)}<button onClick={loadMyRequests}>{tr("Повторить")}</button></div> : !baseRequests.length && !myDocReqs.length ? <EmptyState icon={FileText} title={tr("Заявок пока нет")} description={tr("Поданные заявки и решения по ним сохраняются здесь.")} compact /> : <div className={styles.requestList}>{[...baseRequests.map(item => ({ ...item, type: 'base' })), ...myDocReqs.map(item => ({ ...item, type: 'document' }))].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map(item => <div key={`${item.type}-${item.id}`} className={styles.requestItem}><div><strong>{item.type === 'document' ? tr(item.field_label) || tr("Изменение документа") : item.request_type === 'join' ? tr("Вступление в базу") : tr("Смена базы")}</strong><p>{item.type === 'base' ? [item.base_name, item.new_base_name].filter(Boolean).join(' → ') : tr("Проверка документа администратором")}</p>{tr(item.rejection_reason && <p className={styles.rejectionNote}>{tr(item.rejection_reason)}</p>)}<DateTime value={item.created_at} /></div><StatusBadge tone={item.status === 'approved' ? 'success' : item.status === 'rejected' ? 'danger' : 'warning'}>{tr(item.status_display || ({ pending: tr("На рассмотрении"), approved: tr("Подтверждено"), rejected: tr("Отклонено") })[item.status] || item.status)}</StatusBadge></div>)}</div>}</>)}
      </div>)}

      {/* ── Change base modal ── */}      {tr(showChangeBase && (
        <div className={styles.changeBaseOverlay} onClick={(e) => e.target === e.currentTarget && setShowChangeBase(false)}>
          <div className={styles.changeBaseModal} role="dialog" aria-modal="true" aria-label={docChangeTarget ? tr("Изменение документа") : tr("Смена базы")}>
            <SectionHeading icon={ArrowRightLeft} title={tr("Смена базы")} as="h3" />
            <p style={{ fontSize: 13, color: "var(--theme-text-muted, #6b7280)", marginBottom: 14 }}>{tr("Запрос будет отправлен в вашу текущую базу для подтверждения.")}</p>
            <div className={styles.baseTabs}>
              {[{ key: 'topbase', label: "ТОП-Базы", icon: Building2 }, { key: 'chpp', label: "ТЭЦ", icon: Factory }, { key: 'privatepoint', label: "Соц. объекты / МСУ", icon: Landmark }].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  className={`${styles.baseTab} ${changeSelectedBaseType === key ? styles.baseTabActive : ''}`}
                  onClick={() => { setChangeSelectedBaseType(key); setChangeSelectedBaseId(null); setChangeBaseSearch(''); }}
                >
                  <Icon size={15} aria-hidden="true" />{tr(label)}
                </button>
              ))}
            </div>
            <div className={styles.baseSearchWrap} style={{ marginTop: 10 }}>
              <span className={styles.baseSearchIcon}><Search size={16} /></span>
              <input
                className={styles.baseSearchInput}
                placeholder={tr("Поиск базы по названию...")}
                value={changeBaseSearch}
                onChange={(e) => setChangeBaseSearch(e.target.value)}
              />
              {tr(changeBaseSearch && (
                <button className={styles.baseSearchClear} onClick={() => setChangeBaseSearch('')} aria-label={tr("Очистить поиск")}><X size={16} /></button>
              ))}
            </div>
            {tr(!basesLoading && availableChangeBases.length > 0 && (
              <div className={styles.baseCount}>
                {changeBaseSearch
                  ? tr("Найдено: {{value0}} из {{value1}}", { value0: visibleChangeBases.length, value1: availableChangeBases.length })
                  : tr("Всего: {{value0}}", { value0: availableChangeBases.length })}
              </div>
            ))}
            <div className={styles.baseList} role="radiogroup" aria-label={tr("Выбор новой базы")} style={{ marginTop: 10 }}>
              {basesLoading ? (
                <div className={styles.loadingState}>{tr("Загрузка...")}</div>
              ) : availableChangeBases.length === 0 ? (
                <EmptyState icon={Building2} title={tr("Нет доступных баз")} description={tr("В этом разделе пока нет баз для подачи заявки.")} compact />
              ) : visibleChangeBases.length === 0 ? (
                <EmptyState icon={Search} title={tr("Ничего не найдено по запросу «{{value0}}»", { value0: changeBaseSearch })} description={tr("Проверьте название или выберите другой тип объекта.")} compact />
              ) : visibleChangeBases.map(b => (
                <div
                  key={b.id}
                  role="radio" aria-checked={changeSelectedBaseId === b.id} tabIndex={0}
                  onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setChangeSelectedBaseId(b.id); } }}
                  className={`${styles.baseItem} ${changeSelectedBaseId === b.id ? styles.baseItemSelected : ''}`}
                  onClick={() => setChangeSelectedBaseId(b.id)}
                >
                  <div className={`${styles.baseItemDot} ${changeSelectedBaseId === b.id ? styles.baseItemDotSelected : ''}`} />
                  <span className={styles.baseItemName}>{tr(b.name)}</span>
                </div>
              ))}
            </div>
            <div className={styles.changeBtnRow}>
              <button className={styles.btnSecondary} onClick={() => setShowChangeBase(false)} disabled={submittingBaseRequest}>{tr("Отмена")}</button>
              <button className={styles.btnPrimary} onClick={handleSubmitLeaveRequest} disabled={!changeSelectedBaseId || submittingBaseRequest}>
                <Send size={16} aria-hidden="true" />{submittingBaseRequest ? tr("Отправка...") : tr("Отправить запрос")}
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* ── Doc change modal ── */}
      {tr(docChangeTarget && (
        <div className={styles.changeBaseOverlay} onClick={e => e.target === e.currentTarget && setDocChangeTarget(null)}>
          <div className={styles.changeBaseModal} role="dialog" aria-modal="true" aria-label={docChangeTarget ? tr("Изменение документа") : tr("Смена базы")}>
            <SectionHeading icon={FileText} title={docChangeTarget.currentUrl ? tr("Сменить документ") : tr("Загрузить документ")} as="h3" />
            <p style={{ fontSize: 13, color: "var(--theme-text-muted, #6b7280)", marginBottom: 4 }}>{tr("Тип: ")}<strong>{tr(docChangeTarget.label)}</strong>
            </p>
            <p style={{ fontSize: 12, color: "var(--theme-text-subtle, #9ca3af)", marginBottom: 14 }}>
              {docChangeTarget.currentUrl
                ? tr("После отправки ваш QR-пропуск будет скрыт до подтверждения администратором.")
                : tr("Загруженный документ будет отправлен на проверку администратору.")}
            </p>
            {tr(docChangeTarget.currentUrl && (
              <div style={{ marginBottom: 14 }}>
                <p style={{ margin: '0 0 6px', fontSize: 12, color: "var(--theme-text-muted, #6b7280)", fontWeight: 600 }}>{tr("Текущий документ:")}</p>
                {docChangeTarget.currentUrl.toLowerCase().includes('.pdf') ? (
                  <a href={docChangeTarget.currentUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: "var(--theme-primary-text, #0d00f3)" }}>{tr("Открыть PDF ↗")}</a>
                ) : (
                  <img src={docChangeTarget.currentUrl} alt={tr("Текущий документ")} style={{ maxWidth: '100%', maxHeight: 160, objectFit: 'contain', border: "1px solid var(--theme-border, #e5e7eb)", borderRadius: 8, display: 'block' }} />
                )}
              </div>
            ))}
            <p style={{ margin: '0 0 6px', fontSize: 12, color: "var(--theme-text, #374151)", fontWeight: 600 }}>{docChangeTarget.currentUrl ? tr("Новый документ:") : tr("Выберите файл:")}</p>
            <div className={styles.uploadActions}><label htmlFor="driver-doc-file" className={styles.uploadLabel}><Upload size={18} />{tr("Выбрать файл")}</label><label htmlFor="driver-doc-camera" className={styles.uploadLabel}><Camera size={18} />{tr("Сфотографировать")}</label></div><input id="driver-doc-camera" className={styles.cameraFileInput} type="file" accept="image/*" capture="environment" aria-label={tr("Сфотографировать новый документ")} onChange={event => selectDocument(event.target.files?.[0])} />
            <LocalizedFileInput
              id="driver-doc-file" aria-label={tr("Новый документ")}
              type="file"
              accept="image/*,.pdf"
              onChange={event => selectDocument(event.target.files?.[0])}
              style={{ display: 'block', marginBottom: 10, fontSize: 13, width: '100%' }}
            />
            {tr(docChangePreviewUrl && (
              <img src={docChangePreviewUrl} alt={tr("Предпросмотр нового документа")} style={{ maxWidth: '100%', maxHeight: 160, objectFit: 'contain', border: "1px solid var(--theme-border, #e5e7eb)", borderRadius: 8, marginBottom: 10, display: 'block' }} />
            ))}
            {tr(!docChangePreviewUrl && docChangeFile && (
              <p style={{ fontSize: 12, color: "var(--theme-text, #374151)", marginBottom: 10 }}> {tr(docChangeFile.name)}</p>
            ))}
            <div className={styles.changeBtnRow}>
              <button className={styles.btnSecondary} onClick={() => { setDocChangeTarget(null); setDocChangeFile(null); setDocChangePreviewUrl(null); }} disabled={submittingDocChange}>{tr("Отмена")}</button>
              <button className={styles.btnPrimary} onClick={handleDocChangeSubmit} disabled={!docChangeFile || submittingDocChange}>
                {submittingDocChange ? tr("Отправка...") : tr("Подать заявку")}
              </button>
            </div>
          </div>
        </div>
      ))}

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  );
};


export default DriverCabinet;
